import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { useCurrentLanguage } from '../hooks/useTranslation'
import { usePageSeo } from '../hooks/usePageSeo'
import LocalAnswerBlock from '../components/LocalAnswerBlock.jsx'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { CLIENT_PORTAL_URL } from '../constants/external-links'

const HomeSinglePage = () => {
  const t = useTranslation()
  const { isLoading } = useLanguage()
  const language = useCurrentLanguage()
  const navigate = useNavigate()
  const contactPath = language === 'pt' ? '/pt/contact' : language === 'en' ? '/en/contact' : '/contact'
  const location = useLocation()

  usePageSeo({
    enabled: !isLoading,
    title: t('home.seo.homeLanding.title'),
    description: t('home.seo.homeLanding.description'),
    keywords: t('home.seo.homeLanding.keywords'),
    pathname: location.pathname
  })

  useEffect(() => {
    if (isLoading) {
      document.querySelector('script[type="application/ld+json"][data-home-faq]')?.remove()
      return undefined
    }
    const items = t('home.faq.items')
    if (!Array.isArray(items) || items.length === 0) return undefined
    const mainEntity = items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
    const doc = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity
    }
    let el = document.querySelector('script[type="application/ld+json"][data-home-faq]')
    if (!el) {
      el = document.createElement('script')
      el.setAttribute('type', 'application/ld+json')
      el.setAttribute('data-home-faq', '')
      document.head.appendChild(el)
    }
    el.textContent = JSON.stringify(doc)
    return () => {
      document.querySelector('script[type="application/ld+json"][data-home-faq]')?.remove()
    }
  }, [isLoading, language, t])

  const [selectedImage, setSelectedImage] = useState(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [selectedConditionIndex, setSelectedConditionIndex] = useState(null)
  const conditionModalContentRef = useRef(null)
  
  // Video ref for hero background
  const heroVideoRef = useRef(null)
  // Ensure video plays after load
  useEffect(() => {
    const video = heroVideoRef.current
    if (video) {
      // Force load and play
      const handleCanPlay = () => {
        video.play().catch(err => {
          console.warn('Video autoplay prevented:', err)
        })
      }
      
      const handleLoadedMetadata = () => {
        video.play().catch(err => {
          console.warn('Video autoplay prevented:', err)
        })
      }
      
      video.addEventListener('canplay', handleCanPlay)
      video.addEventListener('loadedmetadata', handleLoadedMetadata)
      
      // Force load if not already loading
      if (video.networkState === 0) {
        video.load()
      }
      
      // Try to play immediately if already loaded
      if (video.readyState >= 3) {
        video.play().catch(err => {
          console.warn('Video autoplay prevented:', err)
        })
      }
      
      return () => {
        video.removeEventListener('canplay', handleCanPlay)
        video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      }
    }
  }, [])

  // Handle hash navigation - scroll to section when hash is present in URL
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash
      if (hash) {
        const targetId = hash.replace('#', '')
        // Legacy links to the removed on-page form now go to the Contact page
        if (targetId === 'contact-form') {
          navigate(contactPath, { replace: true })
          return
        }
        const element = document.getElementById(targetId)
        if (element) {
          // Small delay to ensure page is rendered
          setTimeout(() => {
            const headerEl = document.querySelector('header')
            const headerHeight = headerEl?.offsetHeight ?? 56
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
            const offsetPosition = elementPosition - headerHeight - 8
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            })
          }, 100)
        }
      }
    }

    // Handle initial hash on mount
    handleHashNavigation()

    // Handle hash changes (e.g., when navigating from another page)
    window.addEventListener('hashchange', handleHashNavigation)

    return () => {
      window.removeEventListener('hashchange', handleHashNavigation)
    }
  }, [])
  
  // Gallery images array
  const galleryImages = [
    '/img/gallery/building-1024x758.jpeg',
    '/img/gallery/Lidiana625-104-1024x819.jpg',
    '/img/gallery/Lidiana625-123-1024x683.jpg',
    '/img/gallery/Lidiana625-2-1024x1536.jpg',
    '/img/gallery/Lidiana625-42-1024x683.jpg',
    '/img/gallery/Lidiana625-44-1024x683.jpg',
    '/img/gallery/Lidiana625-52-1024x683.jpg',
    '/img/gallery/Lidiana625-61-820x1024.jpg',
    '/img/gallery/Lidiana625-84-1024x819.jpg'
  ]

  // Office gallery images array
  const officeImages = [
    '/img/building/building-768x569.jpeg',
    '/img/office/1_wall-768x736.jpeg',
    '/img/office/2_waiting_room_2-768x610.jpeg',
    '/img/office/3_waiting_room-768x562.jpeg',
    '/img/office/4_coffee_station-768x756.jpeg',
    '/img/office/5_office_1-768x702.jpeg',
    '/img/office/6_office_1-2-768x857.jpeg',
    '/img/office/7_office_2-768x921.jpeg',
    '/img/office/9_office_3-768x516.jpeg',
    '/img/office/10_office_3_3-768x545.jpeg',
    '/img/office/11_office_4_1-768x466.jpeg',
    '/img/office/12_office_4-768x486.jpeg',
    '/img/office/13_offce_683x1024.jpg',
    '/img/gallery/Lidiana625-52-768x512.jpg',
    '/img/gallery/Lidiana625-61-768x960.jpg',
    '/img/gallery/Lidiana625-84-768x615.jpg',
    '/img/gallery/Lidiana625-123-768x512.jpg',
    '/img/gallery/Lidiana625-104-1024x819.jpg',  
    '/img/office/Lidiana625-10-768x512.jpg',
    '/img/office/Lidiana625-1024x683.jpg',
    '/img/office/Lidiana625-106-1024x683.jpg',
    '/img/office/Lidiana625-107-1024x683.jpg',
    '/img/office/Lidiana625-108-1024x683.jpg',
    '/img/office/Lidiana625-109-1024x683.jpg',
    '/img/office/Lidiana625-11-768x1152.jpg',
    '/img/office/Lidiana625-32-1024x683.jpg',
    '/img/office/Lidiana625-106-1536x1024.jpg',
    '/img/office/Lidiana625-107-1536x1024.jpg',
    '/img/office/Lidiana625-108-1536x1024.jpg'
  ]

  // Office gallery carousel state
  const officeCarouselRef = useRef(null)
  const officeCarouselInnerRef = useRef(null)
  const [carouselOffset, setCarouselOffset] = useState(0)
  const autoScrollRef = useRef(null)
  const [isCarouselPaused, setIsCarouselPaused] = useState(false)

  // Auto-scroll effect for office carousel
  useEffect(() => {
    if (!officeCarouselInnerRef.current) return

    let animationId
    let lastTime = performance.now()
    const scrollSpeed = 0.5 // pixels per frame (adjust for speed)

    const animate = (currentTime) => {
      // Pause animation if carousel is paused
      if (isCarouselPaused) {
        lastTime = currentTime // Reset time to prevent jump when resuming
        animationId = requestAnimationFrame(animate)
        return
      }

      const deltaTime = currentTime - lastTime
      lastTime = currentTime

      setCarouselOffset(prev => {
        const container = officeCarouselRef.current
        const carousel = officeCarouselInnerRef.current
        if (!container || !carousel) return prev

        const maxOffset = -(carousel.scrollWidth - container.offsetWidth)
        
        // If we've reached the end, reset to start (infinite loop)
        if (prev <= maxOffset) {
          return 0
        }

        // Continue scrolling
        return prev - scrollSpeed * (deltaTime / 16) // Normalize to 60fps
      })

      animationId = requestAnimationFrame(animate)
    }

    animationId = requestAnimationFrame(animate)

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId)
      }
    }
  }, [isCarouselPaused])

  // Navigation functions for gallery
  const goToPrevious = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? galleryImages.length - 1 : prev - 1
    )
  }

  const goToNext = () => {
    setCurrentImageIndex((prev) => 
      prev === galleryImages.length - 1 ? 0 : prev + 1
    )
  }

  const openImageModal = (imagePath) => {
    const index = galleryImages.indexOf(imagePath)
    setCurrentImageIndex(index)
    setSelectedImage(imagePath)
  }

  const conditionItems = Array.isArray(t('home.conditionsTreat.items'))
    ? t('home.conditionsTreat.items')
    : []
  const selectedCondition =
    selectedConditionIndex !== null && Array.isArray(conditionItems)
      ? conditionItems[selectedConditionIndex]
      : null

  const goToPreviousCondition = () => {
    if (!Array.isArray(conditionItems) || conditionItems.length === 0) return
    setSelectedConditionIndex((prev) =>
      prev === null ? null : prev === 0 ? conditionItems.length - 1 : prev - 1
    )
  }

  const goToNextCondition = () => {
    if (!Array.isArray(conditionItems) || conditionItems.length === 0) return
    setSelectedConditionIndex((prev) =>
      prev === null ? null : prev === conditionItems.length - 1 ? 0 : prev + 1
    )
  }

  useEffect(() => {
    if (selectedConditionIndex === null || !conditionModalContentRef.current) return
    conditionModalContentRef.current.scrollTop = 0
  }, [selectedConditionIndex])

  // Keyboard navigation for gallery and condition modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!selectedImage && selectedConditionIndex === null) return
      
      switch (event.key) {
        case 'ArrowLeft':
          if (selectedImage) {
            event.preventDefault()
            goToPrevious()
          } else if (selectedConditionIndex !== null) {
            event.preventDefault()
            goToPreviousCondition()
          }
          break
        case 'ArrowRight':
          if (selectedImage) {
            event.preventDefault()
            goToNext()
          } else if (selectedConditionIndex !== null) {
            event.preventDefault()
            goToNextCondition()
          }
          break
        case 'Escape':
          event.preventDefault()
          if (selectedImage) setSelectedImage(null)
          if (selectedConditionIndex !== null) setSelectedConditionIndex(null)
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [selectedImage, selectedConditionIndex, conditionItems.length])

  // FAQ data for Services section
  const faqData = [
    {
      question: t('services.faq.insurance.question'),
      answer: t('services.faq.insurance.answer'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      question: t('services.faq.payment.question'),
      answer: t('services.faq.payment.answer'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      )
    },
    {
      question: t('services.faq.contact.question'),
      answer: t('services.faq.contact.answer'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      )
    }
  ]

  // Auto-advance FAQ carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuestion((prev) => (prev + 1) % faqData.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  // Icon helper function for conditions
  const getConditionIcon = (iconName) => {
    const iconClasses = "w-8 h-8 md:w-10 md:h-10 text-primary-darkGreen"
    switch (iconName) {
      case 'depression':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        )
      case 'anxiety':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )
      case 'trauma':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )
      case 'family':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        )
      case 'children':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        )
      case 'ocd':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        )
      case 'neurodiversity':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        )
      case 'immigrant':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'dualDiagnosis':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        )
      case 'parent':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        )
      case 'career':
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )
      default:
        return (
          <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        )
    }
  }

  // Show loading state if translations are still loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-800 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading content...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section with Video Background */}
      <section id="home" className="relative min-h-screen h-[100dvh] overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          {/* Fallback background image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(/img/background/LidianaBackground-1024x683.jpg)'
            }}
          />
          {/* Video background - add your video file to /public/video/hero-background.mp4 */}
          <video
            ref={heroVideoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover min-w-full min-h-full"
            style={{
              objectFit: 'cover',
              objectPosition: 'center'
            }}
            poster="/img/background/LidianaBackground-1024x683.jpg"
            onError={(e) => {
              e.target.style.display = 'none'
            }}
            onLoadedData={(e) => {
              e.target.play().catch(() => {})
            }}
            onCanPlayThrough={(e) => {
              const video = e.target
              if (video.paused) video.play().catch(() => {})
            }}
          >
            <source src="/video/hero-background.mp4" type="video/mp4" />
          </video>
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-black/40 z-10"></div>
        </div>
        
        {/* Content Overlay */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center items-center text-center pt-20 sm:pt-0">
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 drop-shadow-lg px-2" style={{ fontWeight: 700 }}>
            {t('home.hero.mainHeading')}
          </h1>
          <p className="text-base sm:text-xl md:text-2xl lg:text-3xl text-white/95 mb-6 sm:mb-8 max-w-4xl drop-shadow-md px-2">
            {t('home.hero.description')}
          </p>
          <div className="mb-8 flex flex-wrap items-center justify-center gap-3 px-4">
            <Button asChild size="lg" className="bg-primary-darkGreen text-white hover:bg-primary-darkGreen/90">
              <Link to={contactPath}>{t('common.buttons.scheduleConsultation')}</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/80 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 hover:text-white"
            >
              <a href={CLIENT_PORTAL_URL} target="_blank" rel="noopener noreferrer">
                {t('common.nav.clientPortal')}
              </a>
            </Button>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 px-4">
            {/* Trust indicators */}
            <div className="flex items-center bg-gray-200/20 px-3 sm:px-4 py-2 rounded-full text-white backdrop-blur-sm text-xs sm:text-sm">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="whitespace-nowrap">{t('home.badges.licensed')}</span>
            </div>
            <div className="flex items-center bg-gray-200/20 px-3 sm:px-4 py-2 rounded-full text-white backdrop-blur-sm text-xs sm:text-sm">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="whitespace-nowrap">{t('home.badges.delivery')}</span>
            </div>
            <div className="flex items-center bg-gray-200/20 px-3 sm:px-4 py-2 rounded-full text-white backdrop-blur-sm text-xs sm:text-sm">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="whitespace-nowrap">{t('home.badges.bilingual')}</span>
            </div>
            <div className="flex items-center bg-gray-200/20 px-3 sm:px-4 py-2 rounded-full text-white backdrop-blur-sm text-xs sm:text-sm">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="whitespace-nowrap">{t('home.badges.consultation')}</span>
            </div>
          </div>
        </div>
      </section>

      <LocalAnswerBlock />

      {/* New services — EMDR + sliding scale + therapeutic groups */}
      <section
        id="emdr"
        className="relative overflow-hidden bg-[#f3efe7] py-14 md:py-18 border-y border-primary-darkGreen/10"
        aria-labelledby="home-new-services-heading"
      >
        <div className="relative z-10 w-full max-w-7xl 2xl:max-w-[86rem] mx-auto px-4 sm:px-6 lg:px-8">
          <header className="mb-8 md:mb-11 text-center max-w-2xl mx-auto">
            <h2
              id="home-new-services-heading"
              className="text-2xl md:text-3xl xl:text-4xl font-bold text-primary-darkGreen leading-tight"
            >
              {t('home.newServices.title')}
            </h2>
            {t('home.newServices.subtitle') ? (
              <p className="mt-3 text-base md:text-lg text-gray-600 leading-relaxed">
                {t('home.newServices.subtitle')}
              </p>
            ) : null}
          </header>

          {(() => {
            const path = (suffix) =>
              language === 'pt' ? `/pt${suffix}` : language === 'en' ? `/en${suffix}` : suffix

            const affordableBody = Array.isArray(t('home.affordableProgram.paragraphs'))
              ? t('home.affordableProgram.paragraphs')[0]
              : ''

            const services = [
              {
                key: 'emdr',
                to: path('/emdr'),
                eyebrow: t('home.emdr.eyebrow'),
                title: t('home.emdr.columnTitle'),
                tagline: t('home.emdr.tagline'),
                body: t('home.emdr.body'),
                cta: t('home.emdr.learnMore'),
                image: t('home.emdr.image'),
                imageAlt: t('home.emdr.imageAlt'),
                featured: false,
              },
              {
                key: 'affordable',
                to: path('/sliding-scale'),
                eyebrow: t('home.affordableProgram.eyebrow'),
                title: t('home.affordableProgram.columnTitle'),
                tagline: t('home.affordableProgram.headline'),
                body: affordableBody,
                cta: t('home.affordableProgram.learnMore'),
                image: t('home.affordableProgram.image'),
                imageAlt: t('home.affordableProgram.imageAlt'),
                featured: false,
              },
              {
                key: 'groups',
                to: path('/groups'),
                eyebrow: t('home.groupsHighlight.eyebrow'),
                title: t('home.groupsHighlight.columnTitle'),
                tagline: t('home.groupsHighlight.tagline'),
                body: t('home.groupsHighlight.body'),
                cta: t('home.groupsHighlight.learnMore'),
                image: t('home.groupsHighlight.image'),
                imageAlt: t('home.groupsHighlight.imageAlt'),
                featured: true,
              },
            ]

            return (
              <div
                className="flex gap-5 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory scroll-smooth md:grid md:grid-cols-3 md:overflow-visible md:pb-0 md:gap-6 lg:gap-7 [scrollbar-width:thin]"
                role="list"
              >
                {services.map((service) => (
                  <article
                    key={service.key}
                    role="listitem"
                    className={`min-w-[82%] sm:min-w-[70%] md:min-w-0 snap-center ${
                      service.featured ? 'md:-mt-1' : ''
                    }`}
                  >
                    <Link
                      to={service.to}
                      className={`group flex h-full flex-col overflow-hidden rounded-2xl bg-white text-left shadow-[0_10px_30px_-12px_rgba(72,96,57,0.28)] outline-none transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_-14px_rgba(72,96,57,0.35)] focus-visible:ring-2 focus-visible:ring-primary-golden focus-visible:ring-offset-2 ${
                        service.featured
                          ? 'ring-2 ring-primary-golden/70'
                          : 'ring-1 ring-primary-darkGreen/10'
                      }`}
                    >
                      <div className="relative aspect-[5/3] overflow-hidden bg-[#e8e0d4]">
                        <img
                          src={service.image}
                          alt={service.imageAlt}
                          className="h-full w-full object-cover transition duration-500 ease-out group-hover:scale-[1.04]"
                          width={640}
                          height={384}
                          loading="lazy"
                          decoding="async"
                        />
                        <div
                          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-80"
                          aria-hidden
                        />
                        <span
                          className={`absolute left-3 top-3 rounded-full px-3 py-1 text-[0.68rem] font-bold uppercase tracking-[0.14em] shadow-sm ${
                            service.featured
                              ? 'bg-primary-golden text-primary-darkGray'
                              : 'bg-white/95 text-primary-darkGreen'
                          }`}
                        >
                          {service.eyebrow}
                        </span>
                      </div>

                      <div className="flex flex-1 flex-col px-5 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
                        <h3 className="text-xl font-bold leading-snug text-primary-darkGreen sm:text-[1.35rem]">
                          {service.title}
                        </h3>
                        <p className="mt-1.5 text-sm font-semibold leading-snug text-primary-mediumBrown">
                          {service.tagline}
                        </p>
                        <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-600 sm:text-[0.95rem]">
                          {service.body}
                        </p>
                        <span className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary-darkGreen px-4 py-2.5 text-sm font-semibold text-white transition group-hover:bg-[#3a4f2e]">
                          {service.cta}
                          <svg
                            className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </span>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            )
          })()}
        </div>
      </section>

      {/* Team — portrait cards (aligned with /team page) */}
      <section
        id="team"
        className="relative overflow-hidden bg-primary-offWhite py-14 md:py-18 border-y border-primary-darkGreen/10"
        aria-labelledby="home-team-heading"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35]"
          aria-hidden="true"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(74, 95, 58, 0.12), transparent 55%)',
          }}
        />
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="mb-10 md:mb-12 text-center max-w-2xl mx-auto">
            <h2
              id="home-team-heading"
              className="text-2xl md:text-3xl xl:text-4xl font-bold text-primary-darkGreen leading-tight"
            >
              {t('home.team.heading')}
            </h2>
            {t('home.team.description') ? (
              <p className="mt-3 text-base md:text-lg text-gray-600 leading-relaxed">
                {t('home.team.description')}
              </p>
            ) : null}
          </header>

          <ul
            className="grid grid-cols-2 sm:grid-cols-3 gap-5 sm:gap-6 md:gap-8 list-none m-0 p-0 max-w-4xl mx-auto"
            aria-label={t('home.team.heading')}
          >
            {(t('home.team.members') || []).map((member) => {
              const teamPath =
                language === 'pt'
                  ? `/pt/team/${member.slug}`
                  : language === 'en'
                    ? `/en/team/${member.slug}`
                    : `/team/${member.slug}`
              const role = member.cardTitle || member.title

              return (
                <li key={member.slug} className="flex justify-center">
                  <Link
                    to={teamPath}
                    className="group block w-full max-w-[200px] sm:max-w-[220px] rounded-xl overflow-hidden ring-1 ring-primary-darkGreen/10 shadow-sm shadow-black/10 transition-[box-shadow,ring-color,transform] duration-300 ease-out hover:ring-primary-darkGreen/40 hover:shadow-md hover:shadow-black/20 hover:-translate-y-0.5"
                  >
                    <div
                      className="aspect-[3/4] relative bg-neutral-200 overflow-hidden"
                      style={
                        member.cardBackdrop
                          ? { backgroundColor: member.cardBackdrop }
                          : undefined
                      }
                    >
                      <img
                        src={member.image}
                        alt={member.name}
                        loading="lazy"
                        decoding="async"
                        className={
                          member.cardPhotoInset
                            ? 'absolute inset-x-0 top-0 w-full h-[72%] object-cover object-top transition-transform duration-500 ease-out group-hover:scale-[1.03]'
                            : 'absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-[1.03]'
                        }
                      />
                      <div className="absolute inset-x-2.5 bottom-2.5 sm:inset-x-3 sm:bottom-3 bg-white/95 backdrop-blur-[2px] px-2.5 py-2 sm:px-3 sm:py-2.5 shadow-sm rounded-sm text-left pointer-events-none">
                        <h3 className="text-xs sm:text-sm font-bold text-primary-darkGreen leading-snug">
                          {member.name}
                        </h3>
                        {role ? (
                          <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">
                            {role}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                </li>
              )
            })}
          </ul>

          <div className="text-center mt-10 md:mt-12">
            <Button asChild variant="brand" size="lg" className="font-semibold">
              <Link
                to={
                  language === 'pt' ? '/pt/team' : language === 'en' ? '/en/team' : '/team'
                }
              >
                {t('home.team.cta')}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Conditions We Treat Section */}
      <section id="conditions-we-treat" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-4">
              {t('home.conditionsTreat.title')}
            </h2>
            <div className="text-lg text-gray-700 space-y-4">
              {String(t('home.conditionsTreat.description') || '')
                .split(/\n\n+/)
                .map((p) => p.trim())
                .filter(Boolean)
                .map((para, i) => (
                  <p key={i} className="leading-relaxed">
                    {para}
                  </p>
                ))}
            </div>
          </div>

          {/* Conditions Grid - 5 columns layout */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
            {conditionItems.map((condition, index) => (
              <button
                key={index}
                onClick={() => setSelectedConditionIndex(index)}
                className={`bg-white border-2 rounded-lg p-4 md:p-5 hover:shadow-lg transition-all duration-300 text-center group flex flex-col items-center justify-center min-h-[140px] ${
                  selectedConditionIndex === index
                    ? 'border-[rgb(223,176,36)] shadow-lg'
                    : 'border-gray-200 hover:border-primary-darkGreen'
                }`}
              >
                <div className="flex justify-center mb-3">
                  {getConditionIcon(condition.icon)}
                </div>
                <h3 className="text-xs md:text-sm font-semibold text-gray-800 group-hover:text-primary-darkGreen transition-colors leading-tight">
                  {condition.name}
                </h3>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Our Office Section */}
      <section id="our-office" className="py-16 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-2xl font-bold text-primary-darkGreen md:text-3xl">
              {t('home.office.title')}
            </h2>
            <p className="mx-auto max-w-3xl text-pretty text-base leading-relaxed text-gray-700 md:text-lg">
              {t('home.office.description')}
            </p>
          </div>

          {/* Office Gallery Carousel with Navigation Arrows */}
          <div className="relative">
            {/* Left Arrow */}
            <button
              onClick={() => {
                const container = officeCarouselRef.current
                if (!container) return
                const scrollAmount = container.offsetWidth * 0.8 // Scroll 80% of visible width
                setCarouselOffset(prev => Math.min(0, prev + scrollAmount))
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-primary-darkGreen rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
              aria-label="Previous images"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Carousel Container */}
            <div 
              className="overflow-hidden" 
              ref={officeCarouselRef}
              onMouseEnter={() => setIsCarouselPaused(true)}
              onMouseLeave={() => setIsCarouselPaused(false)}
              onTouchStart={() => setIsCarouselPaused(true)}
              onTouchEnd={() => setIsCarouselPaused(false)}
            >
              <div
                ref={officeCarouselInnerRef}
                className="flex gap-4"
                style={{ transform: `translateX(${carouselOffset}px)` }}
              >
                {/* First set of images */}
                {officeImages.map((image, index) => (
                  <div key={`group1-${index}`} className="flex-shrink-0 w-80">
                    <img
                      src={image}
                      alt="VIVA Psychotherapy office"
                      className="w-full h-80 object-cover rounded-lg shadow-md cursor-pointer"
                      draggable={false}
                    />
                  </div>
                ))}
                {/* Duplicate set for seamless infinite loop */}
                {officeImages.map((image, index) => (
                  <div key={`group2-${index}`} className="flex-shrink-0 w-80">
                    <img
                      src={image}
                      alt=""
                      className="w-full h-80 object-cover rounded-lg shadow-md"
                      draggable={false}
                      aria-hidden="true"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => {
                const container = officeCarouselRef.current
                const carousel = officeCarouselInnerRef.current
                if (!container || !carousel) return
                const scrollAmount = container.offsetWidth * 0.8 // Scroll 80% of visible width
                const singleSetWidth = officeImages.length * (320 + 16) // Each image width + gap
                setCarouselOffset(prev => {
                  const newOffset = prev - scrollAmount
                  // Reset to beginning of first set when we pass the end
                  if (newOffset <= -singleSetWidth) {
                    return 0
                  }
                  return newOffset
                })
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-primary-darkGreen rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
              aria-label="Next images"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-darkGreen mb-4">
              {t('home.faq.title')}
            </h2>
          </div>

          <Accordion
            type="multiple"
            className="rounded-lg border border-border bg-card px-4 shadow-sm sm:px-6"
          >
            {(Array.isArray(t('home.faq.items')) ? t('home.faq.items') : []).map((item, index) => (
              <AccordionItem key={index} value={`home-faq-${index}`}>
                <AccordionTrigger className="text-left text-lg font-semibold text-foreground hover:text-primary hover:no-underline md:text-xl">
                  <h3 className="pr-2 font-semibold">{item.question}</h3>
                </AccordionTrigger>
                <AccordionContent forceMount className="text-base text-foreground">
                  <p className="leading-relaxed">{item.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Condition Modal */}
      {selectedCondition && selectedConditionIndex !== null && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setSelectedConditionIndex(null)}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              goToPreviousCondition()
            }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-[60] bg-white/90 hover:bg-white text-primary-darkGreen rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
            aria-label="Previous condition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              goToNextCondition()
            }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-[60] bg-white/90 hover:bg-white text-primary-darkGreen rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
            aria-label="Next condition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div 
            ref={conditionModalContentRef}
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative mx-10 sm:mx-14"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setSelectedConditionIndex(null)}
              className="absolute top-4 right-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-full p-2 transition-colors duration-200 z-10"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm font-medium text-gray-500 tabular-nums pointer-events-none">
              {selectedConditionIndex + 1} / {conditionItems.length}
            </p>

            {/* Modal Content */}
            <div className="p-8 md:p-12 pb-14">
              {/* Icon and Title */}
              <div className="flex items-center mb-6">
                <div className="mr-4">
                  {getConditionIcon(selectedCondition.icon)}
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-primary-darkGreen">
                  {selectedCondition.name}
                </h3>
              </div>

              {/* Description */}
              {selectedCondition.description && (
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  {selectedCondition.description}
                </p>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Common Concerns */}
                <div>
                  <h4 className="text-lg font-semibold text-primary-darkGreen mb-3">
                    {t('home.conditionsTreat.labels.commonConcerns')}
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    {selectedCondition.commonConcerns}
                  </p>
                </div>

                {/* Our Focus */}
                <div>
                  <h4 className="text-lg font-semibold text-primary-darkGreen mb-3">
                    {t('home.conditionsTreat.labels.ourFocus')}
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    {selectedCondition.ourFocus}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quote Section */}
      <section className="bg-gray-100 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl md:text-3xl font-serif text-gray-800 italic mb-4">
            "{t('home.quote.text')}"
          </blockquote>
          <cite className="text-lg text-secondary-800 font-semibold">
            — {t('home.quote.author')}
          </cite>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-primary-darkGreen text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            {t('common.nav.contact')}
          </h2>
          <p className="text-xl mb-8">
            {t('contact.hero.description', 'Ready to take the first step? Contact us today.')}
          </p>
          <Button asChild size="lg" className="bg-white text-primary-darkGreen hover:bg-white/90">
            <Link to={contactPath}>{t('common.buttons.contactUs')}</Link>
          </Button>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))]"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative w-full h-full max-w-7xl max-h-[95vh] flex items-center justify-center">
            <img 
              src={galleryImages[currentImageIndex]} 
              alt="Gallery image" 
              className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl"
            />
            
            <button 
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-colors duration-200 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <button 
              onClick={(e) => {
                e.stopPropagation()
                goToPrevious()
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-colors duration-200 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button 
              onClick={(e) => {
                e.stopPropagation()
                goToNext()
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-colors duration-200 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 text-gray-800 px-3 py-1 rounded-full text-sm font-medium shadow-lg">
              {currentImageIndex + 1} / {galleryImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HomeSinglePage
